import { DestroyRef, Injectable, Type } from '@angular/core'
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar'
import { NavigationEnd, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { filter, Observable, take, takeUntil } from 'rxjs'
import { GbYesNoSnackBarComponent } from '../components/gb-yes-no-snack-bar/gb-yes-no-snack-bar.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ComponentType } from '@angular/cdk/portal'
import { GbOkSnackBarComponent } from '../components/gb-ok-snack-bar/gb-ok-snack-bar.component'
import { GbMultiLineSnackBarComponent } from '../components/gb-multi-line-snack-bar/gb-multi-line-snack-bar.component'

export enum SnackBarType {
  YesNo = 'yesno',
  Ok = 'ok',
  MultiLine = 'multiline',
  Component = 'component',
}

interface SnackBarComponent {
  getActionObservable(): Observable<boolean>
}

@Injectable({
  providedIn: 'root',
})
export class GbNotificationService {
  private readonly DEFAULT_DURATION = 5000
  private snackBarRef?: MatSnackBarRef<SnackBarComponent | TextOnlySnackBar | unknown>
  private snackBarType?: SnackBarType

  constructor(
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private router: Router,
    private destroyRef: DestroyRef,
  ) {}

  // --- Message Overloads & Implementation ---
  message(
    message: string,
    action: string,
    duration?: number,
    translateParams?: object,
  ): MatSnackBarRef<TextOnlySnackBar>
  message(message: string, action?: '', duration?: number, translateParams?: object): void
  message(
    message: string,
    action = '',
    duration?: number,
    translateParams?: object,
  ): MatSnackBarRef<TextOnlySnackBar> | void {
    const ref = this.snackBarOpen(message, action, 'default-notification', duration, translateParams)
    return ref
  }

  // --- Error Overloads & Implementation ---
  error(message: string, action: string, translateParams?: object): MatSnackBarRef<TextOnlySnackBar>
  error(message: string, action?: '', translateParams?: object): void
  error(message: string, action = '', translateParams?: object): MatSnackBarRef<TextOnlySnackBar> | void {
    const ref = this.snackBarOpen(message, action, 'error-notification', undefined, translateParams)
    return ref
  }

  // --- Success Overloads & Implementation ---
  success(message: string, action: string, translateParams?: object): MatSnackBarRef<TextOnlySnackBar>
  success(message: string, action?: '', translateParams?: object): void
  success(message: string, action = '', translateParams?: object): MatSnackBarRef<TextOnlySnackBar> | void {
    const ref = this.snackBarOpen(message, action, 'success-notification', undefined, translateParams)
    return ref
  }

  snackBarOpen(
    message: string,
    action: string,
    panelClass?: string,
    duration?: number,
    translateParams?: object,
  ): MatSnackBarRef<TextOnlySnackBar> {
    const translatedMessage = this.translateService.instant(message, translateParams)
    const translatedAction = action ? this.translateService.instant(action) : undefined

    return this.snackBar.open(translatedMessage, translatedAction, {
      duration: duration ?? this.DEFAULT_DURATION,
      panelClass: panelClass,
    })
  }

  snackBarComponentOpen<T>(component: Type<T>): void {
    this.snackBarType = SnackBarType.Component
    this.snackBarRef = this.snackBar.openFromComponent(component, { duration: 0 }) as MatSnackBarRef<unknown>
    this.handleDismiss(this.snackBarRef)
  }

  isSnackBarOpen(type: SnackBarType): boolean {
    return this.snackBarType === type
  }

  dismissSnackBar(): void {
    this.snackBarRef?.dismiss()
    this.snackBarRef = undefined
    this.snackBarType = undefined
  }

  yesNo(message: string, panelClass?: string): Observable<boolean> {
    this.snackBarType = SnackBarType.YesNo
    return this.openSnackBarComponent(GbYesNoSnackBarComponent, message, panelClass)
  }

  ok(message: string, panelClass?: string): Observable<boolean> {
    this.snackBarType = SnackBarType.Ok
    return this.openSnackBarComponent(GbOkSnackBarComponent, message, panelClass)
  }

  multiLine(title: string, message: string, actionLabel?: string, icon?: string, color?: string): Observable<boolean> {
    this.snackBarType = SnackBarType.MultiLine
    return this.openSnackBarComponent(
      GbMultiLineSnackBarComponent,
      message,
      undefined,
      { title, actionLabel, icon, color },
      8000,
    )
  }

  openSnackBarComponent<T extends SnackBarComponent>(
    component: ComponentType<T>,
    message: string,
    panelClass?: string,
    data?: Record<string, string | undefined>,
    duration = 0,
  ): Observable<boolean> {
    const snackBarRef = this.snackBar.openFromComponent(component, {
      duration,
      panelClass,
      data: { message: this.translateService.instant(message), ...data },
    })

    this.snackBarRef = snackBarRef
    this.handleDismiss(snackBarRef)

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
        takeUntil(snackBarRef.afterDismissed()),
      )
      .subscribe(() => snackBarRef.dismiss())

    return snackBarRef.instance.getActionObservable()
  }

  private handleDismiss(snackBarRef: MatSnackBarRef<unknown>) {
    snackBarRef
      .afterDismissed()
      .pipe(take(1))
      .subscribe(() => {
        if (this.snackBarRef === snackBarRef) {
          this.snackBarRef = undefined
          this.snackBarType = undefined
        }
      })
  }
}
